<div class="p-6 max-w-full mx-auto">
  <!-- Greeting + tenant switch -->
  <div class="mb-5 flex items-start justify-between gap-4 flex-wrap">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900 tracking-tight">
        {{ greeting() }}@if (firstName) {, {{ firstName }}}
      </h1>
      <p class="text-sm text-gray-500 mt-0.5">Here's your week at a glance</p>
    </div>

    <!-- Shown to admins, and to anyone belonging to more than one tenant.
         Turns blue while a tenant is picked, so a narrowed dashboard never looks
         like the whole one — same convention as the CRM and Projects filters. -->
    @if (showTenantFilter) {
    <div class="relative shrink-0">
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors"
        [class.text-blue-500]="selectedProjectTenantId" [class.text-gray-400]="!selectedProjectTenantId"
        fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>

      <select id="dashboard-tenant" aria-label="Filter the dashboard by tenant"
        [ngModel]="selectedProjectTenantId" (ngModelChange)="onTenantFilterChange($event)"
        class="appearance-none w-full min-w-[11rem] max-w-[14rem] truncate pl-9 pr-9 py-2 text-sm font-medium
               rounded-xl border shadow-sm cursor-pointer outline-none transition-all
               focus:ring-2 focus:ring-blue-500/20"
        [class]="selectedProjectTenantId
          ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-300'
          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'">
        <option value="">All tenants</option>
        @for (t of tenants; track t.id) {
        <option [value]="t.id">{{ t.name }}</option>
        }
      </select>

      <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none transition-colors"
        [class.text-blue-500]="selectedProjectTenantId" [class.text-gray-400]="!selectedProjectTenantId"
        fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
    }
  </div>

  <!-- KPI cards -->
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
    <!-- Active projects -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <p class="text-sm text-gray-500">Active projects</p>
      @if (progressLoading()) {
      <div class="h-8 w-12 bg-gray-200 rounded-lg mt-2 animate-pulse"></div>
      } @else {
      <p class="text-3xl font-bold text-gray-900 tabular-nums mt-1">{{ activeProjectsCount }}</p>
      }
    </div>
    <!-- Tasks to do -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <p class="text-sm text-gray-500">Tasks to do</p>
      @if (loadingTodos()) {
      <div class="h-8 w-12 bg-gray-200 rounded-lg mt-2 animate-pulse"></div>
      } @else {
      <p class="text-3xl font-bold text-gray-900 tabular-nums mt-1">{{ stats().todo }}</p>
      @if (stats().dueToday > 0) {
      <p class="text-xs text-blue-600 mt-1">{{ stats().dueToday }} due today</p>
      } @else if (stats().overdue > 0) {
      <p class="text-xs text-red-600 mt-1">{{ stats().overdue }} overdue</p>
      }
      }
    </div>
    <!-- Hours this week -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <p class="text-sm text-gray-500">Hours this week</p>
      @if (hoursLoading()) {
      <div class="h-8 w-24 bg-gray-200 rounded-lg mt-2 animate-pulse"></div>
      } @else {
      <p class="text-3xl font-bold text-gray-900 tabular-nums mt-1">{{ hoursThisWeek() | number:'1.0-1' }}h</p>
      }
    </div>
  </div>

  <!-- My projects + My tasks -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
    <!-- My projects -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-semibold text-gray-900">My projects</h3>
        <a routerLink="/projects" class="text-sm text-blue-600 hover:underline">View all</a>
      </div>
      @if (progressLoading()) {
      <div class="space-y-4">
        @for (i of [1, 2, 3, 4]; track i) {
        <div class="flex items-center gap-3 animate-pulse">
          <span class="w-2.5 h-2.5 rounded-full bg-gray-200 flex-shrink-0"></span>
          <div class="flex-1">
            <div class="flex items-center justify-between">
              <div class="h-3.5 w-32 bg-gray-200 rounded"></div>
              <div class="h-3.5 w-8 bg-gray-200 rounded"></div>
            </div>
            <div class="h-2.5 w-20 bg-gray-100 rounded mt-1.5"></div>
            <div class="h-1.5 w-full bg-gray-100 rounded-full mt-1.5"></div>
          </div>
        </div>
        }
      </div>
      } @else {
      <div class="space-y-4">
        @for (p of projectProgress(); track p.id) {
        <a [routerLink]="['/projects', p.id]" class="block group">
          <div class="flex items-center gap-3">
            <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" [style.background]="p.color"></span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-2">
                <span class="text-sm font-medium text-gray-800 truncate group-hover:text-blue-600 transition-colors">{{ p.name }}</span>
                <span class="text-sm font-semibold text-gray-700 tabular-nums flex-shrink-0">{{ p.pct }}%</span>
              </div>
              <p class="text-xs text-gray-400 truncate">
                {{ p.subtitle }}@if (p.estimate > 0) { · {{ p.encoded | number:'1.0-0' }}h / {{ p.estimate }}h }
              </p>
              <div class="h-1.5 rounded-full bg-gray-100 mt-1.5 overflow-hidden">
                <div class="h-full rounded-full transition-all" [style.width.%]="p.pct" [style.background]="p.color"></div>
              </div>
            </div>
          </div>
        </a>
        } @empty {
        <p class="text-sm text-gray-400 py-6 text-center">No projects yet</p>
        }
      </div>
      }
    </div>

    <!-- My tasks -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-semibold text-gray-900">My tasks</h3>
        <span class="text-sm text-gray-400">Assigned to me</span>
      </div>
      @if (myTasks().length === 0 && (loading() || loadingTodos())) {
      <div class="space-y-2">
        @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="flex items-center gap-3 py-2 animate-pulse">
          <span class="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0"></span>
          <div class="flex-1">
            <div class="h-3.5 w-40 bg-gray-200 rounded"></div>
            <div class="h-2.5 w-24 bg-gray-100 rounded mt-1.5"></div>
          </div>
          <div class="h-5 w-12 bg-gray-100 rounded-md flex-shrink-0"></div>
        </div>
        }
      </div>
      } @else {
      <div class="space-y-0.5">
        @for (t of myTasks(); track t.kind + t.id) {
        @if ($index < 6) {
        <div (click)="openItem(t)"
          [class.animating-disappear]="isItemDisappearing(t.id)"
          class="w-full flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-gray-50 text-left transition-colors cursor-pointer group">
          <button (click)="toggleTaskStatus(t, $event)"
            class="shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer bg-white"
            [class.bg-emerald-500]="t.status === 'DONE'" [class.border-emerald-500]="t.status === 'DONE'"
            [class.border-blue-400]="t.status === 'INPROGRESS' || t.status === 'IN_PROGRESS'"
            [class.border-gray-300]="t.status !== 'DONE' && t.status !== 'INPROGRESS' && t.status !== 'IN_PROGRESS'"
            [class.hover:border-emerald-500]="t.status !== 'DONE'"
            [class.checkbox-active]="t.status === 'DONE'">
            @if (t.status === 'DONE') {
            <svg class="w-3 h-3 text-white checkmark-active" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
            }
          </button>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-800 truncate" [class.line-through]="t.status === 'DONE'" [class.text-gray-400]="t.status === 'DONE'">{{ t.title }}</p>
            <p class="text-xs text-gray-400 truncate" [class.text-gray-300]="t.status === 'DONE'">
              {{ t.subtitle }}@if (t.dueDate) { · {{ formatDate(t.dueDate) }} }
            </p>
          </div>
          @if (t.priority) {
          <span class="text-xs px-2 py-0.5 rounded-md font-medium border flex-shrink-0" [class]="getPriorityBadgeClass(t.priority)">
            {{ t.priority === 'HIGH' ? 'High' : t.priority === 'MEDIUM' ? 'Medium' : 'Low' }}
          </span>
          }
        </div>
        }
        } @empty {
        <p class="text-sm text-gray-400 py-6 text-center">No tasks assigned</p>
        }
      </div>
      }
    </div>
  </div>

  <!-- Upcoming deadlines -->
  <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
    <h3 class="font-semibold text-gray-900 mb-3">Upcoming deadlines</h3>
    @if (upcomingDeadlines().length === 0 && loadingTodos()) {
    <div class="space-y-1">
      @for (i of [1, 2, 3]; track i) {
      <div class="flex items-center gap-4 py-2.5 animate-pulse">
        <div class="w-10 flex-shrink-0">
          <div class="h-5 w-8 bg-gray-200 rounded mx-auto"></div>
          <div class="h-2.5 w-6 bg-gray-100 rounded mx-auto mt-1"></div>
        </div>
        <div class="flex-1">
          <div class="h-3.5 w-44 bg-gray-200 rounded"></div>
          <div class="h-2.5 w-28 bg-gray-100 rounded mt-1.5"></div>
        </div>
      </div>
      }
    </div>
    } @else {
    <div class="divide-y divide-gray-100">
      @for (d of upcomingDeadlines(); track d.id) {
      <div class="flex items-center gap-4 py-2.5">
        <div class="flex flex-col items-center w-10 flex-shrink-0">
          <span class="text-lg font-bold text-gray-900 leading-none">{{ d.date | date:'dd' }}</span>
          <span class="text-[10px] font-semibold text-gray-400 uppercase">{{ d.date | date:'MMM' }}</span>
        </div>
        <div class="min-w-0">
          <p class="text-sm font-medium text-gray-800 truncate">{{ d.title }}</p>
          <p class="text-xs text-gray-400 truncate">{{ d.subtitle }}</p>
        </div>
      </div>
      } @empty {
      <p class="text-sm text-gray-400 py-6 text-center">No upcoming deadlines</p>
      }
    </div>
    }
  </div>
</div>
